/**
 * Deep links for the migration browser (GDN-3875).
 *
 * The data-import tab already carried the session in the URL (`dsession`), but
 * everything inside a session — which resource group is open, which verdict
 * lane is selected, which type the browser is filtered to — lived in component
 * state. Clicking a resource type therefore left the URL unchanged, so the back
 * button did nothing, reload lost the operator's place, and a link could not
 * point a colleague at one group.
 *
 * This hook makes the URL the source of truth for those three axes. Two rules
 * shape it:
 *
 * - **One write per user action.** Opening a group changes three parameters at
 *   once. Writing them through three separate calls would compute each from the
 *   `searchParams` captured at render, so the last write would clobber the
 *   earlier two. Every action here performs a single write instead, which is
 *   also what keeps `tab` and `dsession`: each write copies the whole previous
 *   query string and touches only the three keys below.
 *
 *   The updater form is not a live read. In `react-router-dom` 6.30.3 it is
 *   called with the `searchParams` captured at render, so two writes in one
 *   tick still clobber each other exactly as two plain writes would. The rule
 *   is one write per action, not a guarantee the router provides.
 * - **Push on selection, replace on filtering.** Opening or leaving a group is
 *   a place the operator should be able to come back to. Tweaking a lane or a
 *   type filter is not: pushing those would bury the previous group under a
 *   pile of history entries that all look the same.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * The Source providers surface is a sibling of the resource groups rather than
 * a resource type, so it needs a sentinel of its own. It is internal; the URL
 * spells it `providers`, which cannot collide with a resource type because the
 * types this converter emits are singular (`provider`, `patient`, `encounter`).
 */
export const PROVIDERS_SURFACE = '__providers__';

const TYPE_PARAM = 'dtype';
const LANE_PARAM = 'dlane';
const FILTER_PARAM = 'dfilter';
const PROVIDERS_VALUE = 'providers';

/** The value every filter parameter is absent for. */
const ALL = 'all';

/**
 * The parameters this browser owns. Anything that closes a session or leaves
 * the tab clears them, so they never leak onto an unrelated surface.
 */
export const MIGRATION_BROWSER_PARAMS = [TYPE_PARAM, LANE_PARAM, FILTER_PARAM] as const;

export interface MigrationBrowserParams {
  /** The open group: a resource type, `PROVIDERS_SURFACE`, or null for the Overview. */
  openType: string | null;
  /** The selected verdict lane, or 'all'. */
  lane: string;
  /** The browser's own type filter, or 'all'. */
  typeFilter: string;
  /** Enter a resource group: scopes the filter to it and clears the lane. */
  openGroup: (resourceType: string) => void;
  /** Return to the Overview, leaving the type filter as the operator set it. */
  closeGroup: () => void;
  /** Open a surface directly, without touching the lane or the type filter. */
  setOpenSurface: (surface: string | null) => void;
  setLane: (lane: string) => void;
  setTypeFilter: (resourceType: string) => void;
  /**
   * Drop an open group that the session does not have, without adding a history
   * entry — going back should not return to a URL that never worked.
   */
  dropOpenType: () => void;
  /** Drop a type filter the session does not have, the same way and for the same reason. */
  dropTypeFilter: () => void;
}

function decodeOpenType(raw: string | null): string | null {
  if (raw === null || raw === '') return null;
  if (raw === PROVIDERS_VALUE) return PROVIDERS_SURFACE;
  // The sentinel is an internal value with a URL spelling of its own. Accepting
  // it raw would let a hand-written link reach the surface by a second name,
  // which is the leak the encoder exists to prevent, only inbound.
  if (raw === PROVIDERS_SURFACE) return null;
  return raw;
}

function encodeOpenType(surface: string | null): string | null {
  if (surface === null) return null;
  return surface === PROVIDERS_SURFACE ? PROVIDERS_VALUE : surface;
}

export function useMigrationBrowserParams(): MigrationBrowserParams {
  const [searchParams, setSearchParams] = useSearchParams();

  const write = useCallback(
    (changes: Record<string, string | null>, replace = false) => {
      setSearchParams(
        previous => {
          const next = new URLSearchParams(previous);
          for (const [key, value] of Object.entries(changes)) {
            if (value === null) next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace },
      );
    },
    [setSearchParams],
  );

  const openType = decodeOpenType(searchParams.get(TYPE_PARAM));
  const lane = searchParams.get(LANE_PARAM) ?? ALL;
  // An absent filter follows the open group rather than falling back to "all".
  // A link trimmed to `?dsession=…&dtype=patient` should open the patient group
  // the way clicking it does, not an unscoped browser that happens to sit under
  // the patient heading. Because of that, `all` INSIDE a group is a real choice
  // and is written out: dropping it would read as "follow the group" and snap
  // the filter back the moment the operator widened it.
  const typeFilter =
    searchParams.get(FILTER_PARAM) ??
    (openType !== null && openType !== PROVIDERS_SURFACE ? openType : ALL);

  const openGroup = useCallback(
    (resourceType: string) => {
      // Matches what entering a group has always done: scope the browser to the
      // type and drop any lane, so a stale verdict filter cannot follow the
      // operator into a group that has no verdicts (GDN-3851).
      write({ [TYPE_PARAM]: resourceType, [FILTER_PARAM]: resourceType, [LANE_PARAM]: null });
    },
    [write],
  );

  const closeGroup = useCallback(() => {
    // The type filter deliberately survives: leaving a group has never reset
    // it, and the Overview's own browse reads it.
    write({ [TYPE_PARAM]: null, [LANE_PARAM]: null });
  }, [write]);

  const setOpenSurface = useCallback(
    (surface: string | null) => {
      write({ [TYPE_PARAM]: encodeOpenType(surface) });
    },
    [write],
  );

  const setLane = useCallback(
    (next: string) => {
      write({ [LANE_PARAM]: next === ALL ? null : next }, true);
    },
    [write],
  );

  const setTypeFilter = useCallback(
    (next: string) => {
      write({ [FILTER_PARAM]: next }, true);
    },
    [write],
  );

  const dropTypeFilter = useCallback(() => {
    write({ [FILTER_PARAM]: null }, true);
  }, [write]);

  const dropOpenType = useCallback(() => {
    // The type filter goes too. It was scoped to the group being rejected, and
    // the control that would clear it only renders inside an open group, so
    // leaving it would filter the Overview by a type the session does not have
    // with no way to undo it.
    write({ [TYPE_PARAM]: null, [LANE_PARAM]: null, [FILTER_PARAM]: null }, true);
  }, [write]);

  return useMemo(
    () => ({
      openType,
      lane,
      typeFilter,
      openGroup,
      closeGroup,
      setOpenSurface,
      setLane,
      setTypeFilter,
      dropOpenType,
      dropTypeFilter,
    }),
    [
      openType,
      lane,
      typeFilter,
      openGroup,
      closeGroup,
      setOpenSurface,
      setLane,
      setTypeFilter,
      dropOpenType,
      dropTypeFilter,
    ],
  );
}
